import { dayjs } from "../dayjs/index.js";
/**
 * Domain-agnostic anonymisation primitives, shared between Vision and Aspire.
 *
 * Moved here from `@thalamos/vision-common` so that both Vision (incident
 * anonymisation) and the Aspire warehouse anonymisation layer depend on one
 * source of truth. Vision-specific anonymisers (e.g. the geo/location fuzzer,
 * bound to Vision's `LocationUnbranded` and precision-override types) remain in
 * `@thalamos/vision-common`, which re-exports the primitives below.
 */
/** Pass-through anonymiser: keep the value exactly as-is. */
export const preserveAnonymiser = (value) => value;
/**
 * Reduce a UK postcode to its outward code (the first 2-4 characters before
 * the space), discarding the inward code. Returns null for unparseable input.
 *
 * e.g. "SE5 8NG" -> "SE5", "N1 7GU" -> "N1", "SE17" -> "SE17".
 */
// take the outward code (first 2-4 non-whitespace characters) of the postcode
export const fuzzPostcode = (postcode) => {
    const match = postcode
        .toUpperCase()
        .match(/^([0-9A-Z]{2,4}) {0,1}([0-9][A-Z]{2})?$/);
    return match ? match[1] : null;
};
/**
 * Convert a date of birth into an NHS-style age band (e.g. "30-34", "90+").
 *
 * Accepts the `{ value, isUnknown? }` wrapper used across our forms. When the
 * input carries an `isUnknown` flag the result echoes `isUnknown: false`;
 * otherwise `isUnknown` is `undefined` (preserving the original behaviour).
 */
export const defaultAgeAnonymiser = (dob) => {
    const { value } = dob;
    if (!value)
        return { value: null, isUnknown: true };
    const parsed = dayjs(value);
    const unknownable = "isUnknown" in dob;
    if (!parsed.isValid())
        return { value: "invalid", isUnknown: unknownable ? false : undefined };
    const yearsAge = dayjs().diff(parsed, "years");
    let ageBracket = "";
    // Child (0-17)
    if (yearsAge < 5)
        ageBracket = "0-4";
    else if (yearsAge < 10)
        ageBracket = "5-9";
    else if (yearsAge < 15)
        ageBracket = "10-14";
    else if (yearsAge < 18)
        ageBracket = "15-17";
    // Young Adult (18-29)
    else if (yearsAge < 20)
        ageBracket = "18-19";
    else if (yearsAge < 25)
        ageBracket = "20-24";
    else if (yearsAge < 30)
        ageBracket = "25-29";
    //  Adult (30-44)
    else if (yearsAge < 35)
        ageBracket = "30-34";
    else if (yearsAge < 40)
        ageBracket = "35-39";
    else if (yearsAge < 45)
        ageBracket = "40-44";
    //  Middle Aged (45-64)
    else if (yearsAge < 50)
        ageBracket = "45-49";
    else if (yearsAge < 55)
        ageBracket = "50-54";
    else if (yearsAge < 60)
        ageBracket = "55-59";
    else if (yearsAge < 65)
        ageBracket = "60-64";
    // Senior (65-89)
    else if (yearsAge < 70)
        ageBracket = "65-69";
    else if (yearsAge < 75)
        ageBracket = "70-74";
    else if (yearsAge < 80)
        ageBracket = "75-79";
    else if (yearsAge < 85)
        ageBracket = "80-84";
    else if (yearsAge < 90)
        ageBracket = "85-89";
    else
        ageBracket = "90+";
    return {
        value: ageBracket,
        isUnknown: unknownable ? false : undefined,
    };
};
//# sourceMappingURL=index.js.map